import { useEffect } from 'react';
import { THEME_PREFERENCES } from '@/styles/constants';
import type { ThemePreferenceType, ThemePreferenceValue } from '@/styles/types';

const STORAGE_KEY = 'ui.theme-preference';

const VALID_THEME_PREFERENCES: readonly string[] =
  Object.values(THEME_PREFERENCES);

// safeLocalStorage JSON-stringifies on write — e.newValue is JSON, not raw.
export const useStorageThemeListener = (
  setThemePreference: (next: ThemePreferenceType) => void,
  setThemePreferenceValue: (next: ThemePreferenceValue) => void
): void => {
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(e.newValue);
      } catch {
        console.warn('[theme-sync] dropping non-JSON storage payload', {
          newValue: e.newValue,
        });
        return;
      }
      if (
        typeof parsed !== 'string' ||
        !VALID_THEME_PREFERENCES.includes(parsed)
      ) {
        console.warn('[theme-sync] dropping unknown theme value', { parsed });
        return;
      }
      const next = parsed as ThemePreferenceType;
      setThemePreference(next);
      if (next !== THEME_PREFERENCES.SYSTEM) {
        setThemePreferenceValue(next as ThemePreferenceValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return (): void => window.removeEventListener('storage', onStorage);
  }, [setThemePreference, setThemePreferenceValue]);
};
